/**
 * BatchDrawer (S4.3) — the 裝櫃批次 drill-down from the workbench.
 *
 * Read-only projection of one batch-lens row (shared `selectBatchLensRows`
 * numbers — utilization and CBM equal the shipping page by construction).
 * All write-back (cut-off, depart, corrections) happens on the 裝櫃批次
 * module the footer button links to.
 */

import { Button, Descriptions, Progress, Tag, Typography, theme } from 'antd';
import { ExportOutlined, WarningOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Drawer } from 'antd';
import { useOccBatchLens } from '@/hooks/useOcc';
import type { ShipmentBatchStatus } from '@/types/api/shipping';
import { controlHeightTokens } from '@/styles/tokens';
import { renderDash } from '@/components/common/TableContainer';
import { LensFooter } from './lensShared';

const { Text } = Typography;

const STATUS_TAG_COLOR: Record<ShipmentBatchStatus, string> = {
  open: 'default',
  cut_off: 'orange',
  departed: 'cyan',
  closed: 'green',
};

export interface BatchDrawerProps {
  open: boolean;
  batchId: string | null;
  syncedAt?: string;
  onClose: () => void;
}

const BatchDrawer = ({ open, batchId, syncedAt, onClose }: BatchDrawerProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation(['occ', 'common']);
  const { token } = theme.useToken();
  const { rows } = useOccBatchLens();

  const batch = rows.find((row) => row.batchId === batchId) ?? null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={480}
      destroyOnHidden
      title={
        batch ? (
          <div className="tw-flex tw-items-center tw-gap-2">
            <span>{batch.batchNumber}</span>
            <Tag color={STATUS_TAG_COLOR[batch.status]} style={{ margin: 0 }}>
              {t(`occ:occ.batches.status.${batch.status}`)}
            </Tag>
          </div>
        ) : (
          t('occ:occ.batchDrawer.title')
        )
      }
    >
      {batch && (
        <>
          <Descriptions column={1} size="small" style={{ marginBottom: token.marginLG }}>
            <Descriptions.Item label={t('occ:occ.batchDrawer.container')}>
              {renderDash(batch.containerNumber)}
            </Descriptions.Item>
            <Descriptions.Item label={t('occ:occ.batchDrawer.seal')}>
              {renderDash(batch.sealNumber)}
            </Descriptions.Item>
            <Descriptions.Item label={t('occ:occ.batchDrawer.etd')}>
              {renderDash(batch.etd)}
            </Descriptions.Item>
            <Descriptions.Item label={t('occ:occ.batchDrawer.eta')}>
              {renderDash(batch.eta)}
            </Descriptions.Item>
            <Descriptions.Item label={t('occ:occ.batchDrawer.cutOffAt')}>
              {renderDash(batch.cutOffAt)}
            </Descriptions.Item>
            <Descriptions.Item label={t('occ:occ.batchDrawer.departedAt')}>
              {renderDash(batch.departedAt)}
            </Descriptions.Item>
            <Descriptions.Item label={t('occ:occ.batchDrawer.cbmTotal')}>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{batch.cbmTotal}</span>
            </Descriptions.Item>
          </Descriptions>

          {/* Live utilization — % bar + the loaded/planned numbers (non-colour). */}
          <div style={{ marginBottom: token.marginLG }}>
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {t('occ:occ.batchDrawer.utilization')}
            </Text>
            <div className="tw-flex tw-items-center tw-gap-2">
              <Progress
                percent={Math.min(100, batch.loadedPct)}
                showInfo={false}
                size="small"
                style={{ flex: 1, margin: 0 }}
              />
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{batch.loadedPct}%</span>
            </div>
            <Text style={{ fontSize: token.fontSizeSM, fontVariantNumeric: 'tabular-nums' }}>
              {t('occ:occ.batchDrawer.plannedLoaded')}: {batch.qtyLoaded} / {batch.qtyPlanned}
            </Text>
          </div>

          {batch.shortLoadedLines > 0 && (
            <div
              className="tw-flex tw-items-center tw-gap-2"
              style={{ marginBottom: token.marginLG, color: token.colorError }}
            >
              <WarningOutlined aria-label={t('occ:occ.batchDrawer.shortLoadedLines')} />
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                {t('occ:occ.batchDrawer.shortLoadedLines')}: {batch.shortLoadedLines}
              </span>
            </div>
          )}

          <Button
            icon={<ExportOutlined />}
            size="large"
            style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
            onClick={() => navigate('/auth/shipments')}
          >
            {t('occ:occ.batchDrawer.goToBatches')}
          </Button>

          <LensFooter syncedAt={syncedAt} />
        </>
      )}
    </Drawer>
  );
};

export default BatchDrawer;
